/**
 * «Мои счета» гостя (UC-G.13) — реальный GET /v1/me/folio (#338).
 *
 * Гость видит ТОЛЬКО свои счета (ownership на бэке), их позиции, статусы и
 * общий неоплаченный итог. Read-only: гость не платит онлайн и не правит
 * счёт — итоговая оплата на стойке (ТЗ §«оплата-как-статус»).
 */
import { Button, Card, Skeleton } from '@hsc/ui';
import { FOLIO_KIND_LABELS, FOLIO_STATUS_LABELS } from '@hsc/types';
import type { FolioDetail, FolioLineItem } from '@hsc/types';
import { useMyFolio } from './useBilling.js';
import { formatDateTime, formatMoney } from './format.js';

function statusTone(status: string): string {
  if (status === 'settled' || status === 'closed') return 'fnb-charge__state--done';
  if (status === 'disputed' || status === 'written_off') return 'fnb-charge__state--off';
  return 'fnb-charge__state--active';
}

function LineRow({ line, currency }: { line: FolioLineItem; currency: string }): JSX.Element {
  return (
    <li className="fnb-charge" data-testid={`folio-line-${line.id}`}>
      <div className="fnb-charge__main">
        <span
          className="fnb-charge__title"
          style={line.reversed ? { textDecoration: 'line-through' } : undefined}
        >
          {FOLIO_KIND_LABELS[line.kind] ?? line.kind}: {line.description}
        </span>
        <span className="fnb-charge__meta">
          {line.qty} × {formatMoney(line.unit_price, line.currency || currency)} ·{' '}
          {formatDateTime(line.posted_at)}
          {line.reversed ? ' · сторнировано' : ''}
        </span>
      </div>
      <span className="fnb-charge__title">
        {formatMoney(line.total, line.currency || currency)}
      </span>
    </li>
  );
}

function FolioCard({ folio }: { folio: FolioDetail }): JSX.Element {
  return (
    <Card data-testid={`folio-card-${folio.id}`}>
      <div className="folio-card__head">
        <h2 className="hsc-card__title">Счёт {folio.id.slice(0, 8)}</h2>
        <span
          className={`fnb-charge__state ${statusTone(folio.status)}`}
          data-testid="folio-status"
        >
          {FOLIO_STATUS_LABELS[folio.status] ?? folio.status}
        </span>
      </div>
      {folio.lines.length === 0 ? (
        <p className="ta-muted">Начислений пока нет.</p>
      ) : (
        <ul className="fnb-charges" data-testid="folio-lines">
          {folio.lines.map((l) => (
            <LineRow key={l.id} line={l} currency={folio.currency} />
          ))}
        </ul>
      )}
      <div className="folio-card__totals" data-testid="folio-totals">
        <div>
          <span className="fnb-charge__meta">Начислено</span>
          <span className="fnb-charge__title">
            {formatMoney(folio.total_charges, folio.currency)}
          </span>
        </div>
        <div>
          <span className="fnb-charge__meta">Оплачено</span>
          <span className="fnb-charge__title">
            {formatMoney(folio.total_payments, folio.currency)}
          </span>
        </div>
        <div>
          <span className="fnb-charge__meta">К оплате</span>
          <span className="folio-unpaid" data-testid="folio-balance">
            {formatMoney(folio.unpaid, folio.currency)}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function MyFolioPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useMyFolio();

  return (
    <section data-testid="my-folio-page">
      <h1 className="hsc-page__title">Мои счета</h1>
      <p className="ta-muted">
        Ваши начисления за проживание и услуги. Оплата — на стойке регистрации.
      </p>

      {isLoading ? (
        <ul className="fnb-charges" data-testid="my-folio-skeleton" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <li key={i} className="fnb-charge fnb-charge--skeleton">
              <Skeleton height={16} radius={6} />
            </li>
          ))}
        </ul>
      ) : isError || data === undefined ? (
        <Card data-testid="my-folio-error">
          <p role="alert">Не удалось загрузить счета. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
          </div>
        </Card>
      ) : data.folios.length === 0 ? (
        <Card data-testid="my-folio-empty">
          <p>Счетов пока нет — начисления появятся по мере заказа услуг.</p>
        </Card>
      ) : (
        <>
          <Card data-testid="my-folio-summary">
            <div className="folio-summary">
              <span className="fnb-charge__meta">Всего к оплате</span>
              <span className="folio-unpaid folio-unpaid--big" data-testid="my-folio-total-unpaid">
                {formatMoney(data.total_unpaid, data.currency)}
              </span>
            </div>
          </Card>
          <div className="folio-list" data-testid="my-folio-list">
            {data.folios.map((f) => (
              <FolioCard key={f.id} folio={f} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
